<template>
    <div class="user-activities">
        <AdminLayoutComponent :date-filter="false">
            <div class="px-4 sm:px-6 lg:px-8">
                <div class="grid grid-cols-4 gap-4 items-end mb-8">
                    <InputFieldComponent
                        v-model="searchParams.email"
                        label="Email address"
                        name="email"
                        type="text"
                        placeholder="you@example.com"
                        :prepend-icon="MailIcon"
                    />

                    <SelectBoxComponent
                        v-model="searchParams.event"
                        label="Event"
                        value-type="string"
                        :options="eventTypes"
                    />

                    <DatePickerComponent
                        id="date_start"
                        v-bind="datePickerProps"
                        v-model="searchParams.date"
                    />

                    <div>
                        <ButtonComponent
                            v-bind="buttonProps"
                            label="Search"
                            type="primary-btn"
                            :disabled="isLoading"
                            @click="onGetActivities"
                        />

                        <ButtonComponent
                            v-bind="buttonProps"
                            label="Reset"
                            type="secondary-btn"
                            :disabled="isLoading"
                            @click="resetSearchParams"
                        />
                    </div>
                </div>

                <div v-if="!isLoading" class="flex flex-col">
                    <div class="-my-2 -mx-4 sm:-mx-6 lg:-mx-8">
                        <div
                            v-if="!_.isEmpty(userActivities.items)"
                            class="table-container w-full"
                        >
                            <div class="table-box overflow-auto">
                                <table>
                                    <thead class="bg-gray-50">
                                        <tr>
                                            <th
                                                v-for="(
                                                    header, index
                                                ) in headers"
                                                :key="index"
                                                scope="col"
                                            >
                                                {{ header }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody
                                        class="
                                            divide-y divide-gray-200
                                            bg-white
                                        "
                                    >
                                        <tr
                                            v-for="activity in userActivities.items"
                                            :key="activity.id"
                                        >
                                            <td>
                                                {{ activity.email }}
                                            </td>
                                            <td>
                                                {{ activity.event }}
                                            </td>
                                            <!-- <td>
                                                {{ activity.name }}
                                            </td> -->
                                            <td>
                                                {{ activity.date }}
                                            </td>
                                            <td>
                                                <p>
                                                    IP ADDRESS:
                                                    {{ activity.ip_address }}
                                                </p>

                                                <p>
                                                    USER AGENT:
                                                    {{ activity.user_agent }}
                                                </p>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <PaginationComponent
                                v-if="!_.isEmpty(userActivities)"
                                v-model="searchParams.page"
                                class="absolute mt-2"
                                :is-loading="isLoading"
                                :total-page="
                                    userActivities.pagination.total_page
                                "
                                @page-clicked="onPaginate($event)"
                            />
                        </div>
                        <EmptyStateComponent
                            v-else
                            class="initial-landing__list mt-4"
                            v-bind="emptyStateProps"
                        />
                    </div>
                </div>
                <PlaceholderComponent v-else type="table" :items="1" />
            </div>
        </AdminLayoutComponent>
    </div>
</template>

<script>
import { computed, reactive } from "vue";
import { useStore } from "vuex";
import AdminLayoutComponent from "@Layouts/admin/";
import { CalendarIcon, MailIcon, UserGroupIcon } from "@heroicons/vue/solid";

import {
    PaginationComponent,
    PlaceholderComponent,
    InputFieldComponent,
    DatePickerComponent,
    EmptyStateComponent,
    SelectBoxComponent,
} from "@CommonComponents";

export default {
    name: "UserActivities",
    components: {
        AdminLayoutComponent,
        PaginationComponent,
        PlaceholderComponent,
        InputFieldComponent,
        DatePickerComponent,
        SelectBoxComponent,
        EmptyStateComponent,
    },
    setup() {
        const store = useStore();

        const initialState = {
            email: null,
            event: null,
            date: null,
            page: 1,
        };

        const searchParams = reactive({ ...initialState });

        const eventTypes = [
            { name: "Login", value: "logged_id" },
            { name: "Export", value: "export" },
        ];

        const userActivities = computed(() => {
            return store.getters["User/userActivities"];
        });

        const isLoading = computed(() => {
            return store.getters["User/isLoading"];
        });

        const isLoadingMore = computed(() => {
            return store.getters["User/isLoadingMore"];
        });

        const datePickerProps = computed(() => {
            return {
                id: "date-picker",
                placeholder: "Select Date",
                minimumView: "day",
                maximumView: "year",
                format: "MMM d, yyyy",
                calendarButtonIcon: CalendarIcon,
            };
        });

        const buttonProps = computed(() => {
            return {
                isLoading: isLoading.value,
                class: "w-auto mx-1 justify-center",
            };
        });

        const emptyStateProps = computed(() => {
            return {
                icon: UserGroupIcon,
                subtitle: "No user logs found",
                class: "",
            };
        });

        if (!_.isEmpty(userActivities.value)) {
            searchParams.page = _.cloneDeep(
                userActivities.value.pagination.current_page
            );
        }

        const headers = [ "Email", "Event", "Date", "Device" ];

        const onPaginate = (page) => {
            searchParams.page = page;
            onGetActivities();
        };

        const resetSearchParams = () => {
            const page = searchParams.page;
            Object.assign(searchParams, initialState);
            searchParams.page = page;
            onGetActivities();
        };

        const onGetActivities = () => {
            store.dispatch("User/getUsersActivities", { ...searchParams });
        };

        return {
            headers,
            userActivities,
            isLoading,
            isLoadingMore,
            searchParams,
            datePickerProps,
            eventTypes,
            buttonProps,
            emptyStateProps,
            resetSearchParams,
            onGetActivities,
            MailIcon,
            onPaginate,
        };
    },
};
</script>

<style lang="scss">
.user-activities {
    .table-container {
        @apply inline-block min-w-full py-2 align-middle md:px-6 lg:px-8 relative;
    }
    .table-box {
        @apply shadow ring-1 ring-black ring-opacity-5 md:rounded-lg;
    }
    table {
        @apply min-w-full divide-y divide-gray-300;
        th {
            @apply px-3 py-3.5 text-left text-sm font-semibold text-gray-900;
        }
        td {
            @apply whitespace-nowrap px-3 py-4 text-sm text-gray-500;
        }
    }
    .pagination_component {
        width: calc(100% - 64px);
        .__container {
            @apply pl-0 #{!important};
        }
        .__pagination_box {
            div:first-child {
                @apply hidden;
            }
        }
    }
}
</style>